import Service, { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import ENV from 'lumen/config/environment';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';

type MenuItem = {
  children: {
    active: boolean;
    badge?: string;
    badgeType?: 'brand';
    className?: string;
    dataTestId?: string;
    externalUrl?: string;
    featureName?: string;
    iconName?: string;
    label: string;
    query?: string;
    queryParams?: Record<string, unknown>;
    route: string;
  }[];
  class?: string;
  dataTestId?: string;
  key: string | undefined;
  name: string | undefined;
};

export default class TabService extends Service {
  @service declare access: Services['access'];
  @service declare destination: Services['destination'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare navigation: Services['navigation'];
  @service declare router: Services['router'];

  month = dayjs.utc().format('YYYY-MM');

  get activeTabName(): string {
    for (const item of this.menuItems) {
      for (const child of item.children) {
        if (child.active) {
          return child.label;
        }
      }
    }
    return this.intl.t('menu.settings');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get benchmarkInsightLightOrUpsellTab() {
    const benchmarkInsightTabTranslation = this.intl.t('demand.benchmark');

    const hasBenchmarkInsightLight =
      this.hasFeatureForNavigation('app.benchmark_insight') &&
      this.hotelAndMarketService.hotel?.benchmarkInsightIsLight;

    return {
      label: benchmarkInsightTabTranslation,
      route: 'hotel.benchmark-insight.upsell',
      active: hasBenchmarkInsightLight
        ? this.isBenchmarkInsightActive
        : this.isBenchmarkInsightUpsellActive,
      badge: this.intl.t('benchmark_insight.free'),
      badgeType: 'brand',
      dataTestId: 'hotel-benchmark-insight-tab',
      featureName: 'app.benchmark_insight',
    };
  }

  get hasBenchmarkInsight(): boolean {
    return this.hasFeatureForNavigation('app.benchmark_insight');
  }

  get hasBIProducts(): boolean {
    return (
      this.hasFeatureForNavigation('app.revenue_insight') ||
      this.hasFeatureForNavigation('app.spider') ||
      this.hasFeatureForNavigation(
        'app.business_intelligence.budget_and_forecast',
      )
    );
  }

  get hasChannelManager(): boolean {
    return this.hasFeatureForNavigation('app.channel_manager');
  }

  get hasChannelManagerTab(): boolean {
    return this.hasChannelManager;
  }

  get hasErnest(): boolean {
    return this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.ERNEST);
  }

  get hasMarketInsight(): boolean {
    return this.hasFeatureForNavigation('app.market_insight');
  }

  get hasMarketSnapshots(): boolean {
    return (
      this.hasFeatureForNavigation('app.market.reservations') &&
      this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.BI_SNAPSHOTS)
    );
  }

  get hasMultipleMarketBIProducts(): boolean {
    const marketBIProductAccess = [
      this.hasFeatureForNavigation('app.market.revenue_insight'),
      this.hasFeatureForNavigation('app.market.spider'),
      this.hasFeatureForNavigation('spider.market.segment_drilldown'),
      this.hasFeatureForNavigation('spider.market.heatmap'),
      this.hasFeatureForNavigation('app.market.total_revenue'),
      this.hasFeatureForNavigation(
        'app.business_intelligence.custom_reporting',
      ),
      this.hasMarketSnapshots,
    ].filter((hasAccess) => hasAccess);

    return marketBIProductAccess.length > 1;
  }

  get hasOverviewDashboard(): boolean {
    return this.hasFeatureForNavigation('overview.view');
  }

  get hasParityInsight(): boolean {
    if (this.hasUnifiedHotelViewToggle) {
      return this.navigation.hasFeatureInSomeSubscription('app.parity');
    }

    return this.access.hasParity;
  }

  get hasPortfolioSmartInsights(): boolean {
    return this.hasFeatureForNavigation('app.smart_insights');
  }

  get hasPricingAssistant(): boolean {
    if (this.isHidePrimaEnabled) {
      return false;
    }

    return this.hasFeatureForNavigation('app.pricing_assistant');
  }

  get hasRanking(): boolean {
    if (this.hasUnifiedHotelViewToggle) {
      return this.navigation.hasFeatureInSomeSubscription('app.ranking');
    }

    return this.access.hasRanking;
  }

  get hasRateInsight(): boolean {
    if (this.hasUnifiedHotelViewToggle) {
      return this.navigation.hasFeatureInSomeSubscription('app.rates');
    }

    return this.access.hasRates;
  }

  get hasRateStrategyAccess(): boolean {
    return this.hasFeatureForNavigation('app.rate_strategy');
  }

  get hasReservationManager(): boolean {
    return this.hasFeatureForNavigation('app.reservation_manager');
  }

  get hasRevenueInsight(): boolean {
    return this.hasFeatureForNavigation('app.market.revenue_insight');
  }

  get hasReviewManager(): boolean {
    return this.hasFeatureForNavigation('app.review_manager');
  }

  get hasShortTermRentalAccess(): boolean {
    return this.hasFeatureForNavigation('app.short_term_rental');
  }

  get hasSmartDistribution(): boolean {
    if (this.isHidePrimaEnabled) {
      return false;
    }

    return this.hasFeatureForNavigation('app.smart_distribution');
  }

  get hasSmartDistributionTab(): boolean {
    return this.hasSmartDistribution;
  }

  private get hasUnifiedHotelViewToggle(): boolean {
    return (
      this.isHotelRoute &&
      this.featureToggle.featureFlagsAvailable &&
      this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW)
    );
  }

  get isBenchmarkInsightActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.benchmark-insight');
  }

  get isBenchmarkInsightUpsellActive(): boolean {
    return this.router.currentRouteName!.includes(
      'hotel.benchmark-insight.upsell',
    );
  }

  get isChannelManagerActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.channel-manager');
  }

  // Temporary FF: hides PRIMA for subscriptions that have both PRIMA and Smart Insights
  // until we implement recommendations that work for both products.
  private get isHidePrimaEnabled(): boolean {
    return this.hasFeatureForNavigation(
      'smart_insights.recommendations_hide_prima',
    );
  }

  get isHotelBenchmarkActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.benchmark');
  }

  get isHotelBIUpsellActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.bi-upsell');
  }

  get isHotelEventsActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.events');
  }

  get isHotelOverviewDashboardActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.overview');
  }

  get isHotelParityActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.parity');
  }

  get isHotelPricingAssistantActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.pricing-manager');
  }

  get isHotelRankingActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.ranking');
  }

  get isHotelRatesActive(): boolean {
    return (
      this.router.currentRouteName!.includes('hotel.rates') ||
      this.router.currentRouteName!.includes('hotel.pricing-manager')
    );
  }

  get isHotelRateStrategyActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.strategy');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isHotelReviewManagerActive() {
    return this.router.currentRouteName!.includes('hotel.reviews');
  }

  get isHotelRIActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.revenue-insight');
  }

  get isHotelRICancellationsActive(): boolean {
    if (!this.isHotelRIActive) {
      return false;
    }

    return (
      this.router.currentRoute?.queryParams['selectedView'] === 'cancellations'
    );
  }

  get isHotelRINetBookingsActive(): boolean {
    if (!this.isHotelRIActive) {
      return false;
    }

    const selectedView = this.router.currentRoute?.queryParams['selectedView'];

    return selectedView === 'net' || selectedView === undefined;
  }

  private get isHotelRoute(): boolean {
    return !!this.hotelAndMarketService.hotel;
  }

  get isHotelShortTermRentalActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.short-term-rental');
  }

  get isHotelShortTermRentalUpsellActive(): boolean {
    return this.router.currentRouteName!.includes(
      'hotel.short-term-rental-upsell',
    );
  }

  get isMarketInsightActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.market-insight');
  }

  get isMarketInsightMarketUnsupported(): boolean {
    return this.hasFeatureForNavigation('app.market_insight.no_data');
  }

  get isMarketInsightUnsupportedMarketActive(): boolean {
    return this.router.currentRouteName!.includes(
      'hotel.market-insight-unsupported-market',
    );
  }

  get isMarketInsightUpsellActive(): boolean {
    return this.router.currentRouteName!.includes(
      'hotel.market-insight-upsell',
    );
  }

  get isMarketParityActive(): boolean {
    return this.router.currentRouteName!.includes('market.parity.insight');
  }

  get isMarketParityManagerActive(): boolean {
    return this.router.currentRouteName!.includes(
      'market.parity.parity-manager',
    );
  }

  get isMarketParityStrategyActive(): boolean {
    return this.router.currentRouteName!.startsWith('market.parity.strategy');
  }

  get isMarketRatesActive(): boolean {
    return this.router.currentRouteName!.includes('market.marketrates');
  }

  get isMarketRevenueActive(): boolean {
    return this.router.currentRouteName!.includes('market.revenue-insight');
  }

  get isMarketSnapshotsActive(): boolean {
    return this.router.currentRouteName!.includes('market.snapshots');
  }

  get isMarketSpiderActive(): boolean {
    return this.router.currentRouteName!.includes('market.executive');
  }

  get isPortfolioSmartInsightsActive(): boolean {
    return this.router.currentRouteName!.includes('market.smart-insights');
  }

  get isPricingAndDistributionFlow(): boolean {
    return (
      this.hasFeatureForNavigation('app.channel_manager') &&
      this.hasPricingAssistant
    );
  }

  get isReservationManagerActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.reservation-manager');
  }

  get isSmartDistributionActive(): boolean {
    return this.router.currentRouteName!.includes('hotel.channel-pricing');
  }

  get isTotalRevenueActive(): boolean {
    return this.router.currentRouteName!.includes('market.total-revenue');
  }

  get menuItems(): MenuItem[] {
    const results: MenuItem[] = [];
    const currentRouteName = this.router.currentRouteName!;
    // NEW: Products-to-Plans navigation structure
    // Variables for readability
    const shouldShowEvents =
      !this.isPricingAndDistributionFlow || this.hasRateInsight;

    if (this.hotelAndMarketService.hotel) {
      if (this.hasErnest) {
        results.push({
          name: 'ernest',
          key: this.intl.t('general.ernest'),
          children: [
            {
              label: this.intl.t('general.ernest'),
              route: '',
              externalUrl: ENV.ernestURL,
              active: false,
              badge: this.intl.t('general.beta.2'),
              badgeType: 'brand',
            },
          ],
        });
      }

      if (this.hasOverviewDashboard) {
        results.push({
          name: 'Overview',
          key: this.intl.t('menu.overview'),
          children: [
            {
              label: this.intl.t('menu.overview'),
              route: 'hotel.overview',
              active: this.isHotelOverviewDashboardActive,
              featureName: 'overview.view',
            },
          ],
        });
      }

      if (this.hasMarketInsight) {
        results.push({
          name: 'market',
          key: this.intl.t('menu.market'),
          children: [
            {
              label: this.intl.t('menu.market'),
              route: 'hotel.market-insight',
              active: this.isMarketInsightActive,
              featureName: 'app.market_insight',
            },
          ],
        });
      } else if (this.hasFeatureForNavigation('app.market_insight_upsell')) {
        results.push({
          name: 'market',
          key: this.intl.t('menu.market'),
          children: [
            {
              label: this.intl.t('menu.market'),
              route: 'hotel.market-insight-upsell',
              iconName: 'lock',
              active: this.isMarketInsightUpsellActive,
              featureName: 'app.market_insight_upsell',
            },
          ],
        });
      } else if (this.isMarketInsightMarketUnsupported) {
        results.push({
          name: 'market',
          key: this.intl.t('menu.market'),
          children: [
            {
              label: this.intl.t('menu.market'),
              route: 'hotel.market-insight-unsupported-market',
              active: this.isMarketInsightUnsupportedMarketActive,
              featureName: 'app.market_insight.no_data',
            },
          ],
        });
      }

      let tabName: string | undefined;
      let tabKey: string | undefined;

      if (this.hasRateInsight) {
        // NEW: Products-to-Plans navigation structure
        // - Pricing & Distribution replaces Rates tabs label to pricing_distribution
        if (this.isPricingAndDistributionFlow) {
          tabName = 'pricing-distribution';
          tabKey = this.intl.t(tKey('menu.pricing_distribution'));
        } else {
          tabName = 'rates';
          tabKey = this.intl.t(tKey('menu.rates'));
        }
      } else if (this.hasParityInsight) {
        tabName = 'parity';
        tabKey = this.intl.t(tKey('menu.parity'));
      }

      const ratesTabs = {
        name: tabName,
        key: tabKey,
        children: [],
      };

      if (this.hasRateInsight) {
        let ratesNavItemRoute = 'hotel.rates.index';
        // NEW: Products-to-Plans navigation structure
        // - Pricing & Distribution replaces Rates label to "monthly_view"
        const label = this.isPricingAndDistributionFlow
          ? this.intl.t('menu.monthly_view')
          : this.intl.t('menu.rates');
        let featureName = 'app.rates';

        if (this.hasPricingAssistant) {
          // In the combination of PA + Rates, we need to make it look like PA is a part of the Rates product
          // This is why we're "hijacking" the Rates nav item here, as PA should be the default if both are available
          // Both products are actually on separate routes to be able to manage complexity
          ratesNavItemRoute = 'hotel.pricing-manager';
          featureName = 'app.pricing_assistant';
        }

        ratesTabs.children.push({
          label,
          route: ratesNavItemRoute,
          query: this.month,
          active: this.isHotelRatesActive,
          featureName: featureName,
        } as never);

        if (this.hasSmartDistribution && this.isPricingAndDistributionFlow) {
          // NEW: Products-to-Plans navigation structure
          // - Adds Smart Distribution in Pricing & Distribution/Rates group
          ratesTabs.children.push({
            label: this.intl.t('menu.weekly_view'),
            route: 'hotel.channel-pricing',
            active: this.isSmartDistributionActive,
            featureName: 'app.smart_distribution',
          } as never);
        }
      }

      if (this.hasShortTermRentalAccess) {
        ratesTabs.children.push({
          label: this.intl.t('menu.short_term_rental'),
          route: 'hotel.short-term-rental',
          active: this.isHotelShortTermRentalActive,
          featureName: 'app.short_term_rental',
        } as never);
      } else if (this.hasFeatureForNavigation('short_term_rental.upsell')) {
        ratesTabs.children.push({
          label: this.intl.t('menu.short_term_rental'),
          route: 'hotel.short-term-rental-upsell',
          active: this.isHotelShortTermRentalUpsellActive,
          featureName: 'short_term_rental.upsell',
        } as never);
      }

      if (this.hasRateStrategyAccess) {
        ratesTabs.children.push({
          label: this.intl.t('menu.strategy'),
          route: 'hotel.strategy.index',
          active: this.isHotelRateStrategyActive,
          featureName: 'app.rate_strategy',
        } as never);
      }

      if (this.isPricingAndDistributionFlow) {
        // NEW: Products-to-Plans navigation structure
        // - Adds Smart Distribution in Pricing & Distribution/Rates group
        if (this.hasFeatureForNavigation('app.events') && shouldShowEvents) {
          ratesTabs.children.push({
            label: this.intl.t('menu.events'),
            route: 'hotel.events.index',
            query: this.month,
            active: this.isHotelEventsActive,
            featureName: 'app.events',
          } as never);
        }
      } else {
        // LEGACY: Events does not depend on Pricing & Distribution conditions
        if (this.hasFeatureForNavigation('app.events')) {
          ratesTabs.children.push({
            label: this.intl.t('menu.events'),
            route: 'hotel.events.index',
            query: this.month,
            active: this.isHotelEventsActive,
            featureName: 'app.events',
          } as never);
        }
      }

      if (this.hasParityInsight || this.access.hasParityAnalyticsStaff) {
        ratesTabs.children.push({
          label: this.intl.t('menu.parity'),
          route: 'hotel.parity.list',
          active: this.isHotelParityActive,
          featureName: 'app.parity',
        } as never);
      }

      if (this.hasRanking) {
        ratesTabs.children.push({
          label: this.intl.t('ranking.ranking'),
          route: 'hotel.ranking',
          active: this.isHotelRankingActive,
          featureName: 'app.ranking',
        } as never);
      }

      if (this.hasFeatureForNavigation('app.benchmark')) {
        ratesTabs.children.push({
          label: this.intl.t('demand.benchmark'),
          route: 'hotel.benchmark.index',
          active: this.isHotelBenchmarkActive,
          featureName: 'app.benchmark',
        } as never);
      }

      // This extra check is needed because we the PriMa product can be standalone or "as a part of rates"
      // In case of standalone there should be a separate PA nav item
      // If the user has access to PriMa & rates, they should go via the Rates nav item
      if (!this.isPricingAndDistributionFlow) {
        // LEGACY: PriMa does not depend on Price & Distribution conditions
        if (this.hasPricingAssistant && !this.hasRateInsight) {
          ratesTabs.children.push({
            label: this.intl.t('menu.pricing_assistant'),
            route: 'hotel.pricing-manager',
            active: this.isHotelPricingAssistantActive,
            featureName: 'app.pricing_assistant',
          } as never);
        }
      }

      if (ratesTabs.children.length) {
        results.push(ratesTabs);
      }

      if (
        this.hasFeatureForNavigation('business_intelligence.upsell') &&
        !this.hasBIProducts
      ) {
        results.push({
          name: undefined,
          key: this.intl.t('menu.performance'),
          children: [
            {
              label: this.intl.t('menu.performance'),
              route: 'hotel.bi-upsell',
              active: this.isHotelBIUpsellActive,
              iconName: 'lock',
              dataTestId: 'hotel-bi-upsell-tab',
              featureName: 'business_intelligence.upsell',
            },
          ],
        });
      }

      if (this.hasBIProducts) {
        const biTabs = {
          name: 'bi',
          key: this.intl.t('menu.bi'),
          dataTestId: 'navigation-menu-bi',
          children: [] as MenuItem['children'],
        };

        if (this.hasFeatureForNavigation('app.revenue_insight')) {
          biTabs.children.push({
            label: this.intl.t('revenue_insight.view_selection.net_bookings'),
            route: 'hotel.revenue-insight.index',
            active: this.isHotelRINetBookingsActive,
            featureName: 'app.revenue_insight',
            queryParams: { selectedView: 'net' },
          });

          if (this.hasFeatureForNavigation('revenueinsight.cancellations')) {
            biTabs.children.push({
              label: this.intl.t(
                'revenue_insight.view_selection.cancellations_only',
              ),
              route: 'hotel.revenue-insight.index',
              active: this.isHotelRICancellationsActive,
              featureName: 'app.revenue_insight',
              queryParams: { selectedView: 'cancellations' },
            });
          }
        }

        if (this.hasFeatureForNavigation('app.spider')) {
          const spiderItems = [
            {
              label: this.intl.t('menu.spider.snapshot'),
              route: 'hotel.snapshot',
              active: this.router.currentRouteName!.includes('hotel.snapshot'),
              dataTestId: 'navigation-bi-snapshot',
              featureName: 'spider.hotel_snapshot',
            },
            {
              label: this.intl.t('menu.spider.day-by-day'),
              route: 'hotel.day-by-day',
              active:
                this.router.currentRouteName!.includes('hotel.day-by-day'),
              dataTestId: 'navigation-bi-day-by-day',
              featureName: 'spider.day_by_day',
            },
            {
              label: this.intl.t('menu.spider.segment-drilldown'),
              route: 'hotel.segment-drilldown',
              active:
                this.router.currentRouteName === 'hotel.segment-drilldown',
              dataTestId: 'navigation-bi-segment-drilldown',
              featureName: 'spider.market_segment_drilldown',
            },
            {
              label: this.intl.t('menu.spider.accounts'),
              route: 'hotel.accounts',
              active: this.router.currentRouteName === 'hotel.accounts',
              dataTestId: 'navigation-bi-accounts',
              featureName: 'spider.account_analysis',
            },
            {
              label: this.intl.t('menu.spider.revpak'),
              route: 'hotel.revpak',
              active: this.router.currentRouteName!.includes('hotel.revpak'),
              dataTestId: 'navigation-bi-revpak',
              featureName: 'spider.revpak',
            },
          ].filter(({ featureName }) =>
            this.hasFeatureForNavigation(featureName),
          );

          biTabs.children.push(...spiderItems);
        }

        if (
          this.hasFeatureForNavigation(
            'app.business_intelligence.budget_and_forecast',
          )
        ) {
          biTabs.children.push(
            {
              label: this.intl.t('menu.budget'),
              route: 'hotel.budget',
              active: this.router.currentRouteName === 'hotel.budget',
              featureName: 'app.business_intelligence.budget_and_forecast',
            },
            {
              label: this.intl.t('menu.forecast'),
              route: 'hotel.forecast',
              active: this.router.currentRouteName === 'hotel.forecast',
              featureName: 'app.business_intelligence.budget_and_forecast',
            },
          );
        }

        if (biTabs.children.length) {
          results.push(biTabs);
        }
      }

      if (this.isPricingAndDistributionFlow) {
        // NEW: Products-to-Plans navigation structure
        // - Indie products wrapped on Pricing & Distribution and Operations groups
        if (!this.hasRateInsight) {
          const pricingDistributionTabs = {
            name: 'pricing-distribution',
            key: this.intl.t('menu.pricing_distribution'),
            children: [],
          };

          if (this.hasPricingAssistant) {
            pricingDistributionTabs.children.push({
              label: this.intl.t('menu.monthly_view'),
              route: 'hotel.pricing-manager',
              active: this.isHotelPricingAssistantActive,
              featureName: 'app.pricing_assistant',
            } as never);
          }

          if (this.hasSmartDistribution) {
            pricingDistributionTabs.children.push({
              label: this.intl.t('menu.weekly_view'),
              route: 'hotel.channel-pricing',
              active: this.isSmartDistributionActive,
              featureName: 'app.smart_distribution',
            } as never);
          }

          if (this.hasFeatureForNavigation('app.events')) {
            pricingDistributionTabs.children.push({
              label: this.intl.t('menu.events'),
              route: 'hotel.events.index',
              query: this.month,
              active: this.isHotelEventsActive,
              featureName: 'app.events',
            } as never);
          }

          results.push(pricingDistributionTabs);
        }

        if (this.hasChannelManager && !this.hasSmartDistribution) {
          results.push({
            name: 'channel-manager',
            key: this.intl.t('menu.distribution'),
            children: [
              {
                label: this.intl.t('menu.distribution'),
                route: 'hotel.channel-manager',
                active: this.isChannelManagerActive,
                featureName: 'app.channel_manager',
              },
            ],
          });
        }

        if (this.hasReservationManager) {
          const operationTabs = {
            name: 'operations',
            key: this.intl.t('menu.operations'),
            children: [],
          };

          operationTabs.children.push({
            label: this.intl.t('menu.reservations'),
            route: 'hotel.reservation-manager',
            active: this.isReservationManagerActive,
            featureName: 'app.reservation_manager',
          } as never);

          results.push(operationTabs);
        }
      } else {
        // LEGACY: Indie products are separate tabs
        // Not found exception sub so far
        if (this.hasSmartDistribution) {
          results.push({
            name: 'smart-distribution',
            key: this.intl.t('menu.smart_distribution'),
            children: [
              {
                label: this.intl.t('menu.smart_distribution'),
                route: 'hotel.channel-pricing',
                active: this.isSmartDistributionActive,
                badge: this.intl.t('general.new'),
                badgeType: 'brand',
                featureName: 'app.smart_distribution',
              },
            ],
          });
        }

        // Cover exception for some subs that require Channel Manager but
        // do not have the conditions for Price & Distribution plan.
        // As the case of subscription "452".
        if (this.hasChannelManager) {
          results.push({
            name: 'channel-manager',
            key: this.intl.t('menu.distribution'),
            children: [
              {
                label: this.intl.t('menu.distribution'),
                route: 'hotel.channel-manager',
                active: this.isChannelManagerActive,
                featureName: 'app.channel_manager',
              },
            ],
          });
        }

        // Cover exception for some subs that require Reservation Manager but
        // do not have the conditions for Price & Distribution plan.
        // As the case of subscriptions "361424" and "452"
        if (this.hasReservationManager) {
          results.push({
            name: 'reservation-manager',
            key: this.intl.t('menu.reservations'),
            children: [
              {
                label: this.intl.t('menu.reservations'),
                route: 'hotel.reservation-manager',
                active: this.isReservationManagerActive,
                featureName: 'app.reservation_manager',
              },
            ],
          });
        }
      }

      if (this.hasReviewManager) {
        results.push({
          name: 'review-manager',
          key: this.intl.t('menu.reviews'),
          children: [
            {
              label: this.intl.t('menu.reviews'),
              route: 'hotel.reviews',
              active: this.isHotelReviewManagerActive,
              featureName: 'app.review_manager',
            },
          ],
        });
      }

      const benchmarkInsightTabTranslation = this.intl.t('demand.benchmark');

      // Don't add the tab is it's Benchmark Insight Light.
      // When it's Benchmark Insight Light, we add the tab at the end of the tabs,
      // in the template. So that it's always the most-right tab.
      if (
        this.hasFeatureForNavigation('app.benchmark_insight') &&
        !this.showBenchmarkInsightLightOrUpsellTab
      ) {
        results.push({
          name: 'benchmark-insight',
          class: 'product-menu--benchmark-insight',
          key: benchmarkInsightTabTranslation,
          children: [
            {
              label: benchmarkInsightTabTranslation,
              route: 'hotel.benchmark-insight.index',
              active: this.isBenchmarkInsightActive,
              dataTestId: 'hotel-benchmark-insight-tab',
              featureName: 'app.benchmark_insight',
            },
          ],
        });
      }
    }

    if (this.hotelAndMarketService.market) {
      if (this.hasPortfolioSmartInsights) {
        results.push({
          name: 'portfolio-smart-insights',
          key: this.intl.t('menu.portfolio_smart_insights'),
          children: [
            {
              label: this.intl.t('menu.portfolio_smart_insights'),
              route: 'market.smart-insights',
              active: this.isPortfolioSmartInsightsActive,
            },
          ],
        });
      }

      if (this.hasRateInsight) {
        results.push({
          name: 'rates',
          key: this.intl.t('menu.rates'),
          children: [
            {
              label: this.intl.t('menu.rates'),
              route: 'market.marketrates.index',
              query: this.month,
              active: this.isMarketRatesActive,
            },
          ],
        });
      }
      if (this.hasFeatureForNavigation('app.market.parity')) {
        if (this.hasFeatureForNavigation('app.market.parity_strategy')) {
          results.push({
            name: 'parity-insight',
            key: this.intl.t('menu.parity_insight'),
            class: 'navigation-menu--parity-insight',
            children: [
              {
                label: this.intl.t('menu.parity_insight'),
                route: 'market.parity.insight.index',
                active: this.isMarketParityActive,
              },
              {
                label: this.intl.t('menu.parity_strategy'),
                route: 'market.parity.strategy',
                active: this.isMarketParityStrategyActive,
              },
            ],
          });
        } else {
          results.push({
            name: 'parity-insight',
            key: this.intl.t('menu.parity_insight'),
            class: 'navigation-menu--parity-insight',
            children: [
              {
                label: this.intl.t('menu.parity_insight'),
                route: 'market.parity.insight.index',
                active: this.isMarketParityActive,
              },
            ],
          });
        }
      }
      if (this.hasFeatureForNavigation('app.market.parity_manager')) {
        results.push({
          name: 'parity-manager',
          key: this.intl.t('menu.parity_manager'),
          children: [
            {
              label: this.intl.t('menu.parity_manager'),
              route: 'market.parity.parity-manager',
              active: this.isMarketParityManagerActive,
            },
          ],
        });
      }

      const revenueChild = {
        label: this.intl.t('menu.bookings'),
        route: 'market.revenue-insight.index',
        active: this.isMarketRevenueActive,
      } as never;

      const totalRevenueChild = {
        label: this.intl.t('menu.total_revenue'),
        route: 'market.total-revenue',
        active: this.isTotalRevenueActive,
      } as never;

      const customReportingChild = {
        label: this.intl.t('menu.custom_reporting'),
        route: 'market.custom-reporting',
        active: currentRouteName.includes('market.custom-reporting'),
        dataTestId: 'market-custom-reporting-tab',
      } as never;

      const snapshotsChild = {
        label: this.intl.t(tKey('menu.snapshots')),
        route: 'market.snapshots',
        active: this.isMarketSnapshotsActive,
        dataTestId: 'market-snapshots-tab',
      } as never;

      const spiderChildren = [
        {
          label: this.intl.t('menu.property_breakdown'),
          route: 'market.executive',
          active: this.isMarketSpiderActive,
          dataTestId: 'hotel-market-spider-executive-tab',
          featureFlag: 'app.market.spider',
        },
        {
          label: this.intl.t('menu.segment_drilldown'),
          route: 'market.segment-drilldown',
          active: currentRouteName === 'market.segment-drilldown',
          dataTestId: 'hotel-market-spider-segment-drilldown-tab',
          featureFlag: 'spider.market.segment_drilldown',
        },
        {
          label: this.intl.t('menu.heatmap'),
          route: 'market.heatmap',
          active: currentRouteName === 'market.heatmap',
          dataTestId: 'hotel-market-spider-heatmap-tab',
          featureFlag: 'spider.market.heatmap',
        },
      ].filter(({ featureFlag }) =>
        this.hasFeatureForNavigation(featureFlag),
      ) as never[];

      if (this.hasMultipleMarketBIProducts) {
        const result = {
          name: 'bi',
          key: this.intl.t('menu.bi'),
          class: 'navigation-menu--market-bi',
          dataTestId: 'navigation-menu--market-bi',
          children: [],
        };

        if (this.hasFeatureForNavigation('app.market.revenue_insight')) {
          result.children.push(revenueChild);
        }

        if (this.hasFeatureForNavigation('app.market.spider')) {
          result.children.push(...spiderChildren);
        }

        if (this.hasFeatureForNavigation('app.market.total_revenue')) {
          result.children.push(totalRevenueChild);
        }

        if (
          this.hasFeatureForNavigation(
            'app.business_intelligence.custom_reporting',
          )
        ) {
          result.children.push(customReportingChild);
        }

        if (this.hasMarketSnapshots) {
          result.children.push(snapshotsChild);
        }

        results.push(result);
      } else {
        if (this.hasFeatureForNavigation('app.market.revenue_insight')) {
          results.push({
            name: 'revenue',
            key: this.intl.t('menu.revenue'),
            children: [revenueChild],
          });
        }

        if (this.hasFeatureForNavigation('app.market.spider')) {
          if (spiderChildren.length > 1) {
            results.push({
              name: 'bi',
              key: this.intl.t('menu.bi'),
              class: 'navigation-menu--market-bi',
              dataTestId: 'navigation-menu--market-bi',
              children: spiderChildren,
            });
          } else {
            results.push({
              name: undefined,
              key: this.intl.t('menu.bi'),
              children: spiderChildren,
            });
          }
        }

        if (this.hasFeatureForNavigation('app.market.total_revenue')) {
          results.push({
            name: 'total-revenue',
            key: this.intl.t('menu.total_revenue'),
            children: [totalRevenueChild],
          });
        }

        if (
          this.hasFeatureForNavigation(
            'app.business_intelligence.custom_reporting',
          )
        ) {
          results.push({
            name: 'custom-reporting',
            key: this.intl.t('menu.custom_reporting'),
            children: [customReportingChild],
          });
        }

        if (this.hasMarketSnapshots) {
          results.push({
            name: 'snapshots',
            key: this.intl.t(tKey('menu.snapshots')),
            children: [snapshotsChild],
          });
        }
      }
    }

    if (this.destination.destinationSubscription) {
      if (this.hasFeatureForNavigation('app.daas.custom_reporting')) {
        results.push({
          name: 'destination',
          key: this.intl.t('menu.destination'),
          children: [
            {
              label: this.intl.t('menu.destination'),
              route: 'destination.reporting',
              active: currentRouteName.includes('destination.reporting'),
            },
          ],
        });
      }
    }

    return results;
  }

  get showBenchmarkInsightLightOrUpsellTab(): boolean {
    const hasBenchmarkInsightLight =
      this.hasFeatureForNavigation('app.benchmark_insight') &&
      this.hotelAndMarketService.hotel?.benchmarkInsightIsLight;
    const hasBenchmarkInsightUpsell =
      !this.hasBenchmarkInsight &&
      (this.hasFeatureForNavigation('benchmark_insight.light.upsell.app') ||
        this.hasFeatureForNavigation('benchmark_insight.upsell.app'));

    return hasBenchmarkInsightLight || hasBenchmarkInsightUpsell;
  }

  hasFeatureForNavigation(featureName: string): boolean {
    if (this.hasUnifiedHotelViewToggle) {
      return this.navigation.hasFeatureInSomeSubscription(featureName);
    }

    return this.access.hasFeature(featureName);
  }
}

declare module '@ember/service' {
  interface Registry {
    tab: TabService;
  }
}
